import React, { Suspense, useEffect, useRef } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import debounce from 'lodash-es/debounce';

import { useTabText } from 'core/hooks/use-tab-text';
import { OAuthResponse, SignInScreen, oAuthStorage } from 'core/components/auth';
import { Loading } from 'core/components/loading';
import { BranchPlus } from 'branch-plus';
import useSession from 'core/hooks/use-session';
import { useRealSession } from 'core/components/view-as';
import { usePauseTrackingDuringViewAs } from 'core/helpers/analytics-view-as';
import { PolicyRoute, CustomerRoute, BranchPlusPolicyRoute } from 'customer/components/provider';
import { DOCUMENT_ROUTE_PATH, DocumentRedirect } from 'customer/components/document-redirect';
import PreviewClarionDoorRequestDifference from 'customer/components/policy/preview-clariondoor-request-diff';
import { VersionCompare } from 'customer/components/policy-sidebar/versions/version-compare';
import {
  OfferProvider,
  OfferDetails,
  OfferHeader,
  CheckoutSidebar,
  Checkout,
  Purchased,
  PreBindUW,
  OfferCompare,
  CompareArbitraryOffers
} from '../../offer';
// Imported from its own directory rather than the `offer` barrel on purpose: routes.test.jsx
// mocks that barrel with an explicit factory, so a symbol added there arrives here as `undefined`
// — and `<Route path={undefined}>` takes the whole route table down.
import {
  SYNTHETIC_RENEWAL_OFFER_ROUTE_PATH,
  SyntheticRenewalOfferRedirect
} from '../../offer/components/synthetic-renewal-offer-redirect';
const AskPanel = React.lazy(() => import('../../openui-ask/ask-panel'));

const PreBindFarmUW = React.lazy(() =>
  import('../../offer/components/pre-bind-farm-UW').then((m) => ({ default: m.PreBindFarmUW }))
);
const PreBindAutoUW = React.lazy(() =>
  import('../../offer/components/pre-bind-auto-UW').then((m) => ({ default: m.PreBindAutoUW }))
);
import LayoutWithSidebar from './layout-with-sidebar';
import UserProfile from './user-profile';
import SweepAccount from './sweep-account';
import { isSweepAccountsEnabled } from 'core/helpers/sweep-account';
import { isQuoteRevisitEnabled } from 'core/helpers/quote-revisit';
import { CheckoutLeftColumn, ModifyQuoteHeader } from 'quote-revisit';
import AgencyReports from './agency-reports';
import { isAgencyReportsVisible } from 'core/helpers/agency-reports';
import Quote from '../../quote';
import Search from '../../search/search';
import AgencyPortal from '../../agency-portal';
import Tools from '../../tools';
import Pocs from './pocs';
import { ReferralRedirect } from '../../referral-redirect';
import { ProtectedRoute } from './protected-route';

import { identifyUserInAppcues } from 'configure-services';
import { usePocEnabled } from 'core/pocs';
import { OPENUI_ASK_POC } from 'core/pocs/entries/openui';

const routesExcludedFromTokenCheck = ['/', '/signin', '/oauth-response'];

// Landing route. The post-sign-in redirect can race the session bootstrap and drop a signed-in
// user on '/' while their deep link (the page they were on before re-auth) is still pending in
// sessionStorage — sending them to the default page would flash it and lose their place. Honor
// the deep link here instead. The ref guards the read-once getDeepLink so re-renders reuse the
// value captured on mount.
const HomeRedirect = () => {
  const session = useSession();
  const deepLinkRef = useRef();
  if (deepLinkRef.current === undefined) {
    deepLinkRef.current = session.isLoggedIn ? oAuthStorage.getDeepLink() : null;
  }

  if (!session.isLoggedIn) {
    return <Redirect to="/signin" />;
  }
  if (deepLinkRef.current) {
    try {
      // Saved as a full same-origin URL (window.location.href); reduce to a router path
      const { pathname, search, hash } = new URL(deepLinkRef.current);
      return <Redirect to={pathname + search + hash} />;
    } catch {
      // Malformed deep link — fall through to the default landing rather than white-screen
    }
  }
  return <Redirect to={session.showAgencyPortal ? '/portal/updates' : '/search/offers'} />;
};

const Routes = () => {
  const session = useSession();
  // Appcues keeps the signed-in user's own traits while View as is on.
  const realSession = useRealSession();
  usePauseTrackingDuringViewAs();
  const isAskPanelOn = usePocEnabled(OPENUI_ASK_POC);
  useTabText();

  // Amplify doesn't have a straightforward way of checking that the refresh token has expired, so we resort to this.
  useEffect(() => {
    // No need to check for a valid session on the auth routes
    if (routesExcludedFromTokenCheck.includes(window.location.pathname)) {
      return;
    }

    async function validateTokenOrLogout() {
      try {
        // - `fetchAuthSession({ forceRefresh: true })` forces Amplify to refresh the session tokens.
        // - `fetchAuthSession({ forceRefresh: true })` will not itself throw an error if the session is expired.
        // - `getCurrentUser()` is what actually throws an error if the user is not authenticated.
        // - `fetchAuthSession({ forceRefresh: true })` can itself throw network-related errors.
        await fetchAuthSession({ forceRefresh: true });
        await getCurrentUser();
      } catch {
        if (!routesExcludedFromTokenCheck.includes(window.location.pathname)) {
          oAuthStorage.setDeepLink(window.location.href);
        }
        await signOut();
        window.location.href = '/';
      }
    }

    // Check that there's a valid session every 1 minute
    const interval = setInterval(validateTokenOrLogout, 1000 * 60);

    // On clicks, taps, or keypresses, check that there's a valid session -- debounced to 5 seconds for performance reasons
    const debouncedValidateTokenOrLogoutFunction = debounce(validateTokenOrLogout, 5000, {
      leading: true,
      trailing: false,
      maxWait: 5000
    });
    const events = ['mousedown', 'touchstart', 'keydown'];
    const eventListeners = events.map((event) =>
      document.addEventListener(event, debouncedValidateTokenOrLogoutFunction, true)
    );

    return () => {
      clearInterval(interval);
      events.forEach((event, idx) => {
        document.removeEventListener(event, eventListeners[idx]);
      });
    };
  }, []);

  // check to see if page changes
  const location = useLocation();
  useEffect(() => {
    window.analytics?.page && window.analytics?.page();

    identifyUserInAppcues(realSession);
  }, [location, realSession]);

  if (session.loading) {
    return null;
  }

  return (
    <Suspense fallback={<Loading noBackground />}>
      {session.isLoggedIn && isAskPanelOn && <AskPanel />}
      <Switch>
        <Route exact path="/signin" component={SignInScreen} />
        <Route exact path="/oauth-response" component={OAuthResponse} />
        <Route exact path="/" component={HomeRedirect} />
        <ProtectedRoute exact path="/profile" permission={session.isLoggedIn} component={UserProfile} />
        <ProtectedRoute
          exact
          path="/sweep-account"
          permission={session.isLoggedIn && session.canManageSweepAccount && isSweepAccountsEnabled()}
          component={SweepAccount}
        />
        {/* Agency Reports (#36219): same gate as the account-menu item (brand flags + caller type). */}
        <ProtectedRoute
          exact
          path="/agency-reports"
          permission={session.isLoggedIn && isAgencyReportsVisible(session)}
          component={AgencyReports}
        />
        <ProtectedRoute exact path="/quote" permission={session.canQuote} component={Quote} />
        <ProtectedRoute path="/portal" permission={session.showAgencyPortal} component={AgencyPortal} />
        <ProtectedRoute path="/search" permission={session.isLoggedIn} component={Search} />
        {/*
          #35226 — must precede every other `/offer/:offerId` route. A synthetic farm renewal
          offer is a docs-only record with no `quote.offerings`, and the offer page crashes on it;
          its path matches only ids carrying the synthetic suffix, so it shadows nothing else.
        */}
        <ProtectedRoute
          path={SYNTHETIC_RENEWAL_OFFER_ROUTE_PATH}
          permission={session.isLoggedIn}
          component={SyntheticRenewalOfferRedirect}
        />
        <ProtectedRoute exact path="/offer/:offerId/purchased" permission={session.canBind}>
          {({
            history,
            match: {
              params: { offerId }
            }
          }) => <Purchased offerId={offerId} history={history} />}
        </ProtectedRoute>
        <ProtectedRoute exact path="/offer/:offerId/:option/checkout" permission={session.canViewCheckoutPage}>
          {({
            history,
            match: {
              params: { offerId, option }
            }
          }) => (
            <LayoutWithSidebar
              offerId={offerId}
              header={isQuoteRevisitEnabled() ? ModifyQuoteHeader : OfferHeader}
              content={Checkout}
              side={isQuoteRevisitEnabled() ? CheckoutLeftColumn : CheckoutSidebar}
              history={history}
              onBack={() => {
                history.push(`/offer/${offerId}`);
              }}
              option={option}
            />
          )}
        </ProtectedRoute>
        <ProtectedRoute
          exact
          path="/offer/:offerId/details"
          permission={session.canSeeFullOfferDetails}
          component={OfferDetails}
        />
        <ProtectedRoute
          exact
          path="/offer/:offerId/verifyUW"
          permission={session.canEditOffers}
          component={PreBindUW}
        />
        <ProtectedRoute
          exact
          path="/offer/:offerId/verifyFarmUW"
          permission={session.canEditOffers}
          component={PreBindFarmUW}
        />
        <ProtectedRoute
          exact
          path="/offer/:offerId/verifyAutoUW"
          permission={session.canEditOffers}
          component={PreBindAutoUW}
        />
        <ProtectedRoute
          exact
          path="/offer/:offerId/compare"
          permission={session.isInternalAgent && !session.isAgency}
          component={OfferCompare}
        />
        <ProtectedRoute
          exact
          path="/offer/:offerId/compare-arbitrary-offers"
          permission={session.isInternalAgent && !session.isAgency}
          component={CompareArbitraryOffers}
        />
        <ProtectedRoute path="/offer/:offerId" permission={session.canQuote || session.viewOnly}>
          {({
            match: {
              params: { offerId }
            }
          }) => <OfferProvider offerId={offerId} />}
        </ProtectedRoute>
        <ProtectedRoute path="/branch-plus/:policyId" permission={session.isInternalAgent} component={BranchPlus} />
        {/* Must precede the catch-all :policyPreviewId route below so the wildcard can't swallow it. */}
        <ProtectedRoute
          exact
          path="/customer/:id/policy/:policyId/version-compare/:version"
          permission={session.canViewClarionDoorData}
        >
          {({
            match: {
              params: { id, policyId, version }
            }
          }) => <VersionCompare accountId={id} policyId={policyId} version={version} />}
        </ProtectedRoute>
        <ProtectedRoute
          path="/customer/:id/policy/:policyId/:policyPreviewId"
          permission={session.canViewClarionDoorData}
        >
          {({
            history,
            match: {
              params: { id, policyId, policyPreviewId }
            }
          }) => (
            <PreviewClarionDoorRequestDifference
              accountId={id}
              policyId={policyId}
              history={history}
              policyPreviewId={policyPreviewId}
            />
          )}
        </ProtectedRoute>
        <ProtectedRoute permission={session.isLoggedIn} path="/customer/:id/policy/:policyId">
          {({
            history,
            match: {
              params: { id, policyId }
            }
          }) => {
            if (id !== policyId.split('-')[0]) {
              const correctedId = policyId.split('-')[0];
              return <Redirect to={`/customer/${correctedId}/policy/${policyId}`} />;
            }
            return <PolicyRoute accountId={id} policyId={policyId} history={history} />;
          }}
        </ProtectedRoute>
        <ProtectedRoute path="/customer/:id/branch-plus/:policyId" permission={session.isInternalAgent}>
          {({
            history,
            match: {
              params: { id, policyId }
            }
          }) => <BranchPlusPolicyRoute accountId={id} policyId={policyId} history={history} />}
        </ProtectedRoute>
        {/* #37141: a link to one stored document (the agent's endorsement email links to the dec).
            Must precede the `/customer/:id` catch-all below. */}
        <ProtectedRoute exact permission={session.isLoggedIn} path={DOCUMENT_ROUTE_PATH} component={DocumentRedirect} />
        <ProtectedRoute permission={session.isLoggedIn} path="/customer/:id">
          {({
            history,
            match: {
              params: { id }
            }
          }) => <CustomerRoute accountId={id} history={history} />}
        </ProtectedRoute>
        <ProtectedRoute path="/staff/referral" component={ReferralRedirect} permission={session.canQuote} />
        <ProtectedRoute path="/staff/tools" component={Tools} permission={session.canViewTools} />
        <ProtectedRoute exact path="/staff/pocs" component={Pocs} permission={session.isLoggedIn} />
        {/* This handles No Match routes */}
        <Route path="*">
          <Redirect to="/" />
        </Route>
      </Switch>
    </Suspense>
  );
};

export default Routes;
